import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDocumentTitle } from "usehooks-ts";
import { DataTableContextProvider } from "../DataTable/DataTableContext";
import { DataTable } from "../DataTable/DataTable";
import { capitalize } from "lodash";
import { TabbedNav } from "../TabbedNav";
import { executeQuery, REACT_APP_MANAGER_LINK } from "../../../utils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useDeltaToastContext } from "../../../shared/contexts/ToasterContext";
import { EditableTextarea } from "../../../shared/table/editable";
import { IInvoice } from "../../utils/quoteUtils";
import { LinkCell } from "../DataTable/LinkCell";
import { TableHeaderSelectFilter } from "../DataTable/TableHeaderSelectFilter";
import { format } from "date-fns";

export const Heaven2: React.FC = () => {
  const queryClient = useQueryClient();
  const toastContext = useDeltaToastContext();
  const { schoolYear } = useParams();
  const navigate = useNavigate();
  const queryKey = `heaven/${schoolYear}`;

  useDocumentTitle(
    schoolYear ? `Heaven - ${capitalize(schoolYear)}` : "Heaven"
  );

  const updateQuoteMutation = useMutation({
    mutationFn: ({
      quoteNumber,
      field,
      value,
    }: {
      quoteNumber: string | number | null;
      field: string;
      value: number | null;
    }) => {
      const payload: {
        [key: string]: number | null;
      } = {};
      payload[field] = value;
      return executeQuery({
        path: `/manager_new/invoices/${quoteNumber}`,
        requestBody: payload,
        method: "put",
      }) as Promise<{ success: boolean; message: string }>;
    },
    onSuccess: (data) => {
      if (data.success && data.message) {
        queryClient.invalidateQueries({ queryKey: [queryKey] });
      }
    },
    onError: (error) => {
      toastContext.addToast({
        message: error.message || "Unable to update quote",
        status: "Error",
      });
    },
  });

  useEffect(() => {
    if (!schoolYear) {
      navigate("./all", { replace: true });
    } else if (["All", "Unknown"].includes(schoolYear)) {
      navigate(`../heaven2/${schoolYear.toLowerCase()}`, { replace: true });
    }
  }, [schoolYear, navigate]);

  if (!schoolYear) {
    return <></>;
  }

  return (
    <main className="flex flex-col gap-4 p-6">
      <header className="flex items-center gap-8">
        <h1 className="text-2xl font-bold">Heaven</h1>
        <TabbedNav
          choices={[
            { text: "All", href: "../heaven2/all" },
            { text: "2026", href: "../heaven2/2026" },
            { text: "2025", href: "../heaven2/2025" },
            { text: "Unknown", href: "../heaven2/unknown" },
          ]}
        />
      </header>
      <DataTableContextProvider
        key={schoolYear}
        alternateKeyForPersistentState="heaven"
        source={`/manager_new/invoices/activated2/${schoolYear}`}
        queryKey={queryKey}
        defaults={{ sortBy: [{ id: "quoteNumber", desc: true }] }}
        sortable={[
          "quoteNumber",
          "invoiceNumber",
          "poUploadedTime",
          "numberOfStudents",
          "price",
        ]}
        filterable={[
          "invoiceNumber",
          "id",
          "poNumber",
          "name",
          "actualExpiration",
          "adminEmail",
          "contactName",
          "contactEmail",
          "licenseTier",
          "rosteringSystem",
          "lms",
          "integrationStatus",
          "typeFormatted",
        ]}
        globalFilter
        fetchOnLoad
        columnOverrides={{
          quoteNumber: ({ cell: { value: quoteNumber } }) => (
            <LinkCell
              key={quoteNumber}
              value={{
                text: quoteNumber,
                href: `${REACT_APP_MANAGER_LINK}/quote-lookup/${quoteNumber}`,
              }}
            />
          ),
          notes: ({ row }) => (
            <div className="w-48">
              <EditableTextarea
                row={row}
                value={((row as any).original as IInvoice).notes}
                updateMutation={updateQuoteMutation}
              />
            </div>
          ),
        }}
        filterOverrides={{
          actualExpiration: ({ column }) => (
            <TableHeaderSelectFilter
              key={column.id}
              className="!min-w-44"
              choices={[
                1696219140, 1727841540, 1759381140, 1790913540, 1822449540,
                1854071940, 1885607940, 1917143940, 1948679940,
              ]
                .filter((d) => {
                  if (schoolYear === "all" || schoolYear === "unknown") {
                    return true;
                  }
                  const date = new Date(d * 1000);
                  const year = date.getFullYear();
                  return year === Number(schoolYear);
                })
                .map((d) => ({
                  label: format(new Date(d * 1000), "PP"),
                  value: d,
                }))}
              column={column}
              multi
            />
          ),
          licenseTier: ({ column }) => (
            <TableHeaderSelectFilter
              key={column.id}
              className="!min-w-28"
              choices={["Plus", "Integral"]}
              column={column}
            />
          ),
          rosteringSystem: ({ column }) => (
            <TableHeaderSelectFilter
              key={column.id}
              className="!min-w-52"
              choices={["ClassLink/OneRoster", "Clever"]}
              column={column}
            />
          ),
          lms: ({ column }) => (
            <TableHeaderSelectFilter
              key={column.id}
              className="!min-w-40"
              choices={["Canvas", "Schoology"]}
              column={column}
            />
          ),
          integrationStatus: ({ column }) => (
            <TableHeaderSelectFilter
              key={column.id}
              className="!min-w-36"
              choices={["Requested", "Verified"]}
              column={column}
            />
          ),
          typeFormatted: ({ column }) => (
            <TableHeaderSelectFilter
              key={column.id}
              className="!min-w-52"
              choices={[
                "6-12",
                "6-8",
                "7-12",
                "9-12",
                "School",
                "School (No NCES ID)",
                "School License",
                "Individual",
              ]}
              column={column}
              multi
            />
          ),
        }}
      >
        <DataTable />
      </DataTableContextProvider>
    </main>
  );
};
